<script lang="ts">
  import Modal from '../ui/Modal.svelte';
  import FormField from '../ui/FormField.svelte';
  import Button from '../ui/Button.svelte';
  import { SYNC_DIRECTIONS, type IntegrationConfig, type SyncDirection } from '../../lib/types';

  type Props = {
    providerId: string;
    config: IntegrationConfig | null;
    providerName: string;
    onClose: () => void;
    onSave: (providerId: string, config: IntegrationConfig) => void;
  };

  let { providerId, config, providerName, onClose, onSave }: Props = $props();

  let syncOrders = $state(true);
  let syncInventory = $state(true);
  let orderSyncDirection = $state<SyncDirection>('inbound');
  let inventorySyncDirection = $state<SyncDirection>('bidirectional');
  let saving = $state(false);

  $effect(() => {
    if (config) {
      syncOrders = config.syncOrders;
      syncInventory = config.syncInventory;
      orderSyncDirection = config.orderSyncDirection;
      inventorySyncDirection = config.inventorySyncDirection;
    }
  });

  async function handleSave() {
    saving = true;
    try {
      onSave(providerId, {
        syncOrders,
        syncInventory,
        orderSyncDirection,
        inventorySyncDirection,
      });
    } finally {
      saving = false;
    }
  }
</script>

<Modal isOpen={true} title="{providerName} Settings" onClose={onClose}>
  <form onsubmit={(e) => { e.preventDefault(); handleSave(); }}>
    <div class="config-section">
      <h4>Sync Configuration</h4>
      <p class="section-hint">Choose which data to sync and the direction of synchronization.</p>
    </div>

    <div class="toggle-row">
      <label class="toggle-label">
        <input type="checkbox" bind:checked={syncOrders} disabled={saving} />
        <span>Sync Orders</span>
      </label>
      {#if syncOrders}
        <div class="direction-select">
          <FormField label="Order Sync Direction">
            <select bind:value={orderSyncDirection} disabled={saving}>
              {#each SYNC_DIRECTIONS as direction}
                <option value={direction.id}>{direction.label}</option>
              {/each}
            </select>
          </FormField>
        </div>
      {/if}
    </div>

    <div class="toggle-row">
      <label class="toggle-label">
        <input type="checkbox" bind:checked={syncInventory} disabled={saving} />
        <span>Sync Inventory</span>
      </label>
      {#if syncInventory}
        <div class="direction-select">
          <FormField label="Inventory Sync Direction">
            <select bind:value={inventorySyncDirection} disabled={saving}>
              {#each SYNC_DIRECTIONS as direction}
                <option value={direction.id}>{direction.label}</option>
              {/each}
            </select>
          </FormField>
        </div>
      {/if}
    </div>

    <div class="form-actions">
      <Button variant="secondary" onclick={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button type="submit" variant="primary" loading={saving} disabled={saving}>
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  </form>
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .config-section h4 {
    margin: 0 0 0.25rem;
    font-size: 1rem;
    color: var(--color-text, #333);
  }

  .section-hint {
    margin: 0;
    font-size: 0.85rem;
    color: var(--color-text-secondary, #666);
  }

  .toggle-row {
    background: var(--color-surface, #f9fafb);
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: var(--radius-md, 8px);
    padding: 1rem;
  }

  .toggle-label {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.9375rem;
    color: var(--color-text, #333);
  }

  .toggle-label input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #667eea;
    cursor: pointer;
  }

  .direction-select {
    margin-top: 0.75rem;
    padding-left: 1.75rem;
  }

  .direction-select select {
    width: 100%;
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: var(--radius-sm, 6px);
    font-size: 0.9rem;
    font-family: inherit;
    background: white;
    transition: border-color 0.2s;
  }

  .direction-select select:focus {
    outline: none;
    border-color: #667eea;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }
</style>
